import { get } from "svelte/store";
import { sessionStore } from "../../stores/session";
import { navigateToPath } from "../../routing";
import PATHS from "../../constants/routePaths";

export class CaptchaError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "CaptchaError";
  }
}

type AuthFunction = () => Promise<void> | void;

let currentAuthAttempt: { unsubscribe: () => void } | null = null;

// The splash screen is detached from the auth process, but the auth process requires the token to be set.
// So, this function subscribes the store waiting for a token to be set and only then proceeds with the auth function.

// This is also supposed to ensure that, if the user triggers multiple authentications somehow, we:
// - Prevent the auth function from being called multiple times (preventing race conditions as well)
// - Avoid memory leaks (by always running everything to the end, not leaving a promise hanging)

// Common cases:
// - The splash always gets displayed when the microsite is loaded, but if the user skips the landing page,
// the auth process will run right away, but it can't because we don't have a token yet.
// - The user doesn't act after the splash clicking the start button only after the token expires.
export const withCaptcha = async (authFunction: AuthFunction) => {
  const session = get(sessionStore);
  const { retryAt, validUntil } = session.turnstileTokenData ?? {};

  const isRateLimited = retryAt ? new Date(retryAt).getTime() > Date.now() : false;
  const isValidUntilInTheFuture = validUntil ? new Date(validUntil).getTime() > Date.now() : false;

  if (isValidUntilInTheFuture) {
    // If the token is still valid, we can proceed with the auth function
    await authFunction();
  } else if (isRateLimited) {
    // If not valid in the future, and the user is rate limited, we need to navigate to the rate limit page
    // When the assessment call returns 403 or 429, the retryAt will be set and the validUntil will be undefined
    navigateToPath(PATHS.RATE_LIMIT);
  } else {
    // Force the captcha to be displayed (no token data, so not rate limited either)
    sessionStore.update((state) => ({
      ...state,
      turnstileTokenData: undefined
    }));
    
    // Create a new auth attempt
    // This creates an object that will hold the unsubscribe function from the store subscription
    // In the end of every auth attempt, we need to unsubscribe from the store
    // We only actually run the last auth attempt, by saving the last object in currentAuthAttempt
    const authAttempt = { unsubscribe: () => {} };
    currentAuthAttempt = authAttempt;
    
    try {
      // Wait for the token to be set by the captcha component logic
      await new Promise<void>((resolve, reject) => {
        authAttempt.unsubscribe = sessionStore.subscribe((state) => {
          if (state.turnstileTokenData) {
            state.turnstileTokenData.token
              ? resolve()
              : reject(new CaptchaError("Captcha error")); // If captcha fails, the redirect will already be handled
          }
        });
      });

      // Only proceed if this is still the current auth attempt
      if (currentAuthAttempt === authAttempt) {
        await authFunction();
        currentAuthAttempt = null;
      }
    } finally {
      // Make sure we unsubscribe from the store for this specific context
      authAttempt.unsubscribe();
    }
  }
};
